import isEmpty from 'lodash/isEmpty'
import type { Blog } from '@ztechapps/common.contract'
import { useMutation } from '@ztechapps/common.hook'
import { mintBlogPreviewSlug } from '@ztechapps/blog.common'
import { blogPreviewSlugQueries } from '../graphql/blogPreviewSlug'

type SaveSlugInput = Pick<Blog, '_id' | 'slug'>

export type UseBlogPreviewSlugProps = {
  blogId: string
  slug: string
  onSlugSaved?: () => Promise<void> | void
}

export type UseBlogPreviewSlugResult = {
  /** Resolves the post's preview slug, minting and saving one first when it has none; rejects when the save fails. */
  getPreviewSlug: () => Promise<string>
}

/**
 * The slug is the only credential on the preview URL, so a post saved without one gets a minted slug saved first —
 * the preview then shows the post it was opened from. Saving needs blog.canEdit.
 */
export const useBlogPreviewSlug = ({
  blogId,
  slug,
  onSlugSaved,
}: UseBlogPreviewSlugProps): UseBlogPreviewSlugResult => {
  const [onSaveSlug] = useMutation<Blog, SaveSlugInput>(blogPreviewSlugQueries.updateConfig)

  const getPreviewSlug = async () => {
    if (!isEmpty(slug)) return slug
    const mintedSlug = mintBlogPreviewSlug()
    const { errors } = await onSaveSlug({ input: { _id: blogId, slug: mintedSlug } })
    if (errors) throw errors
    await onSlugSaved?.()
    return mintedSlug
  }

  return { getPreviewSlug }
}
